import React, { forwardRef, useCallback } from "react";

import { SEOVideoObject } from "./SEOVideoObject";
import { FullScreenWrapper } from "./FullScreenWrapper";
import useUpdateDisplay from "../hooks/useUpdateDisplay";
import { useAutoplayContext } from "../contexts/AutoplayContext";
import { useAdsElementsRefContext } from "../contexts/AdsElementsRefContext";
import { useSubtitles } from "../hooks/useSubtitles";
import PropTypes from "prop-types";
import { useOrcaCommonContext } from "../contexts/OrcaCommonContext";
import { useTransitionToVideo } from "../hooks/useTransitionToVideo";
import { useHandleVideoStart } from "../hooks/useHandleVideoStart";
import { OrcaCommonUI } from "./OrcaCommonUI";
import { FullControls } from "../../controls/FullControls";
import { useChartbeat } from "../hooks/useChartbeat";
import { AdsUI } from "../ads/AdsUI";

export const OrcaFullUI = forwardRef(
  (
    {
      onVideoRendered = () => {},
      config = {},
      promoEnabled = true,
      liveEventEnded = false,
      handleVideoEnd = () => {},
      isLive = false
    },
    ref
  ) => {
    // Destructure props
    const {
      aspectRatio: _aspectRatio,
      aspectWidth = 16,
      aspectHeight = 9,
      controlWrapperClassName,
      playCallback = () => {},
      isCarousel = false,
      stickyStyles
    } = config;

    const { autoplayState } = useAutoplayContext();
    const { adControlsRef } = useAdsElementsRefContext();
    const { setRenderControls, play, handleLivePreview, autoplay } =
      useOrcaCommonContext();

    const aspectRatio = _aspectRatio || aspectHeight / aspectWidth;

    const transitionToVideo = useTransitionToVideo({
      setRenderControls,
      isCarousel,
      autoplay,
      play,
      playCallback
    });

    const { hasSubtitles, updateCues, currentCue, setupSubtitlesListener } =
      useSubtitles();

    const handleStart = useHandleVideoStart(
      transitionToVideo,
      config,
      setupSubtitlesListener
    );

    const onPromoClick = useCallback(() => {
      if (autoplayState.isLivePreview) {
        handleLivePreview();
      }
      handleStart(autoplayState.isLivePreview);
    }, [handleStart, autoplayState.isLivePreview, handleLivePreview]);

    const updateDisplay = useUpdateDisplay(adControlsRef);

    useChartbeat();

    return (
      <>
        <FullScreenWrapper
          {...{
            controlWrapperClassName,
            liveEventEnded,
            aspectRatio,
            stickyStyles
          }}
        >
          <AdsUI
            {...{
              promoEnabled,
              config,
              onPromoClick,
              updateDisplay
            }}
          >
            {adContainer => (
              <OrcaCommonUI
                onVideoRendered={onVideoRendered}
                config={config}
                promoEnabled={promoEnabled}
                liveEventEnded={liveEventEnded}
                handleVideoEnd={handleVideoEnd}
                isLive={isLive}
                handleStart={handleStart}
                transitionToVideo={transitionToVideo}
                updateCues={updateCues}
                hasSubtitles={hasSubtitles}
                currentCue={currentCue}
                ref={ref}
                Controls={FullControls}
              >
                {adContainer}
              </OrcaCommonUI>
            )}
          </AdsUI>
        </FullScreenWrapper>
        <SEOVideoObject />
      </>
    );
  }
);

OrcaFullUI.displayName = "OrcaFullUI";

OrcaFullUI.propTypes = {
  onVideoRendered: PropTypes.func,
  config: PropTypes.object,
  promoEnabled: PropTypes.bool,
  liveEventEnded: PropTypes.bool,
  handleVideoEnd: PropTypes.func,
  isLive: PropTypes.bool
};
